import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { first, forkJoin } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslocoService } from '@jsverse/transloco';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { toDayBoundaryIso } from 'src/app/shared/helpers/date-helpers';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { ExpenseService } from '../../services/expense.service';
import {
  EMPTY_EXPENSE_GRID_RESULT,
  ExpenseStatus,
  ExportFormat,
  IExpense,
  IExpenseGridResult,
  IExportExpensesRequest,
  ISearchExpensesRequest,
  expenseSortToOrderBy,
  sumExpenseAmounts,
  toExpenseGridResult
} from '../../models/expense';

// Accounting payment run ("Buchhaltung" tab): accepted and paid expenses of every employee, with
// filter, server-side sort, Excel/PDF export and bulk "mark as paid". Gated by Expenses.Accounting
// in the parent; the backend enforces it on the search and mark-paid endpoints.
@UntilDestroy()
@Component({
    selector: 'app-expense-accounting-list',
    templateUrl: './expense-accounting-list.component.html',
    standalone: false
})
export class ExpenseAccountingListComponent implements OnInit {

  expenses: IExpenseGridResult = EMPTY_EXPENSE_GRID_RESULT;
  selectedIds: string[] = [];

  // Amount sum of the displayed page; the grand total of the filtered set comes from the backend.
  get pageAmount(): number {
    return sumExpenseAmounts(this.expenses.data);
  }


  gridState: IGridState = { pageSize: 25, skip: 0 };

  // Server-side sort (date, submitter, expense type, salary payment, status), so the grid can be
  // worked through in export order.
  sort: SortDescriptor[] = [{ field: 'date', dir: 'desc' }];

  // The (paged) request behind the displayed list - reused for reloads and, widened, for exports.
  private lastRequest: ISearchExpensesRequest | null = null;

  ExpenseStatus = ExpenseStatus;

  // Accounting may see every active employee (GetReviewableEmployees resolves that from the permission).
  employees: IEmployee[] = [];

  // Status filter: Accepted (awaiting payout, the default) or Paid; the "all" item (null) means both.
  // Primitive values with a translated text, so the dropdown needs no templates (Kendo requires
  // textField and valueField together). Texts are filled in ngOnInit once the scope is loaded.
  statusFilterItems: { value: ExpenseStatus; text: string }[] = [];
  allStatusesItem: { value: ExpenseStatus | null; text: string } = { value: null, text: '' };

  filterForm = this.fb.group({
    employee: [null],
    status: [ExpenseStatus.Accepted],
    dateRangeStart: [null],
    dateRangeEnd: [null]
  });

  // Dropdown items (Excel / PDF) for both export buttons.
  exportFormatItems = [
    { text: 'Excel', format: ExportFormat.Excel },
    { text: 'PDF', format: ExportFormat.Pdf }
  ];

  // read-only detail dialog
  dialogOpen = false;
  dialogId: string | null = null;

  // reject reason sub-dialog
  rejectOpen = false;
  rejectId: string | null = null;

  get pageSizes() {
    return PageSize;
  }

  constructor(
    private expenseService: ExpenseService,
    private notificationService: AppNotificationService,
    private translocoService: TranslocoService,
    private fb: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    this.statusFilterItems = [ExpenseStatus.Accepted, ExpenseStatus.Paid].map(value => ({
      value,
      text: this.translocoService.translate('expenses.expenseStates.' + ExpenseStatus[value])
    }));
    this.allStatusesItem = { value: null, text: this.translocoService.translate('expenses.filterAllStatuses') };

    this.expenseService.getReviewableEmployees()
      .pipe(first(), untilDestroyed(this))
      .subscribe(employees => this.employees = employees);

    this.onSubmitFilter();
  }

  // --- filter ---
  onSubmitFilter(): void {
    this.selectedIds = [];
    // A new filter always restarts at the first page.
    this.gridState.skip = 0;
    this.lastRequest = this.buildRequest(1, this.gridState.pageSize);
    this.load(this.lastRequest);
  }

  onResetFilter(): void {
    // Back to the default (accepted expenses awaiting payout), not to "all statuses".
    this.filterForm.reset({ status: ExpenseStatus.Accepted });
    this.onSubmitFilter();
  }

  // --- paging (server-side) ---
  onPageChange({ skip, take }: PageChangeEvent): void {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;
    // Selection is per-page; drop it when the visible rows change.
    this.selectedIds = [];
    this.lastRequest = this.buildRequest(Math.round((skip + take) / take), take);
    this.load(this.lastRequest);
  }

  onPageSizeChange(pageSize: number): void {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload(): void {
    this.reload();
  }

  // --- sorting (server-side) ---
  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.gridState.skip = 0;
    this.selectedIds = [];
    this.lastRequest = this.buildRequest(1, this.gridState.pageSize);
    this.load(this.lastRequest);
  }

  private buildRequest(pageNumber: number, pageSize: number): ISearchExpensesRequest {
    const formValues = this.filterForm.value;
    const status: ExpenseStatus | null = formValues.status ?? null;

    const request: ISearchExpensesRequest = {
      pageNumber,
      pageSize,
      orderBy: expenseSortToOrderBy(this.sort),
      statuses: status == null ? [ExpenseStatus.Accepted, ExpenseStatus.Paid] : [status]
    };

    const filters: IFilter[] = [];

    if (formValues.employee?.id) {
      filters.push({ field: 'SubmitterId', operator: 'eq', value: formValues.employee.id });
    }

    if (formValues.dateRangeStart) {
      filters.push({ field: 'Date', operator: 'gte', value: toDayBoundaryIso(formValues.dateRangeStart, false) });
    }

    if (formValues.dateRangeEnd) {
      filters.push({ field: 'Date', operator: 'lte', value: toDayBoundaryIso(formValues.dateRangeEnd, true) });
    }

    if (filters.length === 1) {
      request.advancedFilter = filters[0];
    } else if (filters.length > 1) {
      request.advancedFilter = { logic: 'and', filters };
    }

    return request;
  }

  private load(request: ISearchExpensesRequest): void {
    this.expenseService.searchAccountingExpenses(request)
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => this.expenses = toExpenseGridResult(response));
  }

  private reload(): void {
    if (this.lastRequest) {
      this.load(this.lastRequest);
    }
  }

  // --- export (selection or whole filtered list) ---
  exportAll(item: { format: ExportFormat }): void {
    this.export(null, item.format);
  }

  exportSelected(item: { format: ExportFormat }): void {
    if (!this.selectedIds.length) {
      return;
    }
    this.export(this.selectedIds, item.format);
  }

  private export(selectedExpenseIds: string[] | null, format: ExportFormat): void {
    const request: IExportExpensesRequest = {
      ...(this.lastRequest ?? this.buildRequest(1, this.gridState.pageSize)),
      // The export endpoint resolves its visibility from these two flags (scope "All"), which the
      // backend widens to every employee for the Accounting permission - the same rows as the
      // accounting search. Without them a pure accounting user would export nothing.
      forApproval: true,
      includeOwn: true,
      // Export the whole filtered set, not just the current page.
      pageNumber: 1,
      pageSize: 999999,
      selectedExpenseIds: selectedExpenseIds ?? undefined,
      format
    };

    const fileName = format === ExportFormat.Pdf ? 'Spesen.pdf' : 'Spesen.xlsx';

    this.expenseService.exportExpenses(request)
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => downloadBlob(response, fileName));
  }

  // --- mark paid ---

  // Only accepted rows can be paid out; "select all" may also have picked paid rows.
  get selectedAcceptedIds(): string[] {
    return this.expenses.data
      .filter(e => this.selectedIds.includes(e.id) && e.status === ExpenseStatus.Accepted)
      .map(e => e.id);
  }

  markPaidSelected(): void {
    const ids = this.selectedAcceptedIds;
    if (!ids.length) {
      return;
    }
    // One request per expense (the endpoint takes a single id). A racing re-rejection fails that
    // one with a conflict shown by the error interceptor; the others still go through, so the
    // list is reloaded either way.
    forkJoin(ids.map(id => this.expenseService.markExpensePaid(id)))
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: () => {
          this.selectedIds = [];
          this.notify('expenses.paidManySuccess');
          this.reload();
        },
        error: () => {
          this.selectedIds = [];
          this.reload();
        }
      });
  }

  markPaid(expense: IExpense): void {
    this.expenseService.markExpensePaid(expense.id)
      .pipe(first(), untilDestroyed(this))
      .subscribe(() => {
        this.notify('expenses.paidSuccess');
        this.reload();
      });
  }

  // --- reject ---
  openReject(expense: IExpense): void {
    this.rejectId = expense.id;
    this.rejectOpen = true;
  }

  confirmReject(reason: string): void {
    if (this.rejectId) {
      this.expenseService.rejectExpenseByAccounting({ id: this.rejectId, rejectionReason: reason })
        .pipe(first(), untilDestroyed(this))
        .subscribe(() => {
          this.notify('expenses.rejectedSuccess');
          this.reload();
        });
    }
    this.closeReject();
  }

  closeReject(): void {
    this.rejectOpen = false;
    this.rejectId = null;
  }

  // --- view ---
  statusName(status: ExpenseStatus): string {
    return ExpenseStatus[status];
  }

  statusClass(status: ExpenseStatus): string {
    return 'status-' + ExpenseStatus[status].toLowerCase();
  }

  open(expense: IExpense): void {
    this.dialogId = expense.id;
    this.dialogOpen = true;
  }

  closeDialog(): void {
    this.dialogOpen = false;
    this.dialogId = null;
  }

  private notify(translationKey: string): void {
    this.notificationService.showNotification({
      message: this.translocoService.translate(translationKey),
      style: 'success'
    });
  }
}
